import React, { useState, useEffect } from 'react';

import {
    Edit,
    DeleteOutline,
    Add
} from '@mui/icons-material';

import Container from '../../blocks/container';
import CommonButton from '../../buttons/common-button';
import MediasService from '../../../services/MediasService';
import InfoBox from '../../blocks/messages/info-box';

import DeleteMediaGroupModal from './modals/modal--admin--delete-media-group';
import EditMediaGroupModal from './modals/modal--admin--edit-media-group';
import CreateMediaGroupModal from './modals/modal--admin--create-media-group';

/**
 * MediaGroupsTable Component
 * 
 * @param {object} refreshKey - Key to refresh the component
 * @param {object} refreshData - Function to refresh the parent component
 *  
 * This component renders a table of media groups and allows the user to manage them.
 * @returns {JSX.Element}
 */
const MediaGroupsTable = ({ refreshKey, refreshData }) => {
    // Initialize state variables
    const [modal, setModal] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // State to hold media groups
    const [mediaGroups, setMediaGroups] = useState([]);

    // State to handle pagination
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 5;

    /**
     * Fetch media groups and categories
     * Function to fetch media groups and categories from the API
     * and update the state with the results.
     * @returns {Promise<void>}
     */
    const fetchMediaGroups = async () => {
        try {
            // Set loading state to true
            setLoading(true);

            // Fetch media groups
            const mediaGroups = await MediasService.getMediaGroups();

            // Update media groups state
            setMediaGroups(mediaGroups);
        } catch (error) {
            setError(error?.message || "Une erreur inattendue est survenue");
        } finally {
            setLoading(false);
        }
    };

    // Fetch media groups on component mount
    useEffect(() => {
        fetchMediaGroups();
    }, []);

    // Fetch media groups on refresh
    useEffect(() => {
        fetchMediaGroups();
    }, [refreshKey]);

    // Display a loading message while fetching data
    if (loading) return <div>Chargement...</div>;

    // Display an error message if the data could not be loaded
    if (error) {
        return (
            <InfoBox
                type={'error'}
                disabledCloseBtn={true}
                text={error}
                
            />
        );
    }

    // Calculate the indexes for the current page
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentGroups = mediaGroups.slice(startIndex, endIndex);

    // Pagination buttons
    const totalPages = Math.ceil(mediaGroups.length / itemsPerPage);

    // Handle pagination controls
    const handlePrevPage = () => {
        if (currentPage > 1) {
            setCurrentPage(prevPage => prevPage - 1);
        }
    };

    // Handle pagination controls
    const handleNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(prevPage => prevPage + 1);
        }
    };

    // Get range of pages to display in pagination
    const getPageRange = () => {
        const range = [];
        const start = Math.max(1, currentPage - 1);
        const end = Math.min(totalPages, currentPage + 1);

        for (let i = start; i <= end; i++) {
            range.push(i);
        }

        return range;
    };

    // Handle delete action
    const handleDelete = (groupId) => {
        setModal({type: 'delete', groupId: groupId});
    }

    // Handle edit action
    const handleEdit = (groupId) => {
        setModal({type: 'edit', groupId: groupId});
    }

    // Handle add action
    const handleAdd = () => {
        setModal({type: 'add'});
    }

    // Render the component
    return (
        <React.Fragment>
            <Container className="admin-medias-manager auto-height w-15">
                <h2>Gestion des groupes de catégories de médias</h2>
                {mediaGroups.length > 0 ? (
                    <div className="media-groups-list">

                        {/* Media groups table */}
                        <table className="media-table">
                            <thead>
                                <tr>
                                    <th>Nom</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentGroups.map((group) => (
                                    <tr key={group.id}>
                                        <td>{group.name}</td>
                                        <td>
                                            <button
                                                onClick={() => handleEdit(group.id)}
                                                className="media-table-btn"
                                            >
                                                <Edit />
                                            </button>
                                            <button
                                                onClick={() => handleDelete(group.id)}
                                                className="media-table-btn"
                                            >
                                                <DeleteOutline />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {/* Pagination controls */}
                        <div className="pagination">
                            <button
                                onClick={() => setCurrentPage(1)}
                                disabled={currentPage === 1}
                                className='first pagination-btn'
                            >
                                Première
                            </button>
                            <button
                                onClick={handlePrevPage}
                                disabled={currentPage === 1}
                                className='back pagination-btn'
                            >
                                Précédent
                            </button>
                            {getPageRange().map((page) => (
                                <button
                                    key={page}
                                    onClick={() => setCurrentPage(page)}
                                    className={`pagination-btn ${page === currentPage ? 'active' : ''}`}
                                >
                                    {page}
                                </button>
                            ))}
                            <button
                                onClick={handleNextPage}
                                disabled={currentPage === totalPages}
                                className='next pagination-btn'
                            >
                                Suivant
                            </button>
                            <button
                                onClick={() => setCurrentPage(totalPages)}
                                disabled={currentPage === totalPages}
                                className='last pagination-btn'
                            >
                                Dernière
                            </button>
                        </div>
                    </div>
                ) : (
                    <p className='no-data'>Aucun groupe de catégories trouvé.</p>
                )}
                <CommonButton
                    label="Ajouter un groupe de catégories"
                    icon={<Add />}
                    onClick={handleAdd}
                />
            </Container>

            {modal && modal.type === 'delete' && (
                <DeleteMediaGroupModal open={true} onClose={() => setModal(null)} groupId={modal.groupId} onDelete={() => refreshData()} />
            )}

            {modal && modal.type === 'edit' && (
                <EditMediaGroupModal open={true} onClose={() => setModal(null)} groupId={modal.groupId} onEdit={() => refreshData()} />
            )}

            {modal && modal.type === 'add' && (
                <CreateMediaGroupModal open={true} onClose={() => setModal(null)} onCreate={() => refreshData()} />
            )}
        </React.Fragment>
    );
};

export default MediaGroupsTable;